import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAddDraftStep, useDeleteDraftSteps } from '@/api/mutations/useProcessDraft';
import { DraftNodeNotFoundError, useDraftGraph } from '@/api/draftGraph';
import { isObjectIdHex } from '@/lib/utils';
import { resolveNonOverlappingPosition } from '../graph/position';
import { useProcessDraftCache } from './useProcessDraftCache';

interface UseStepActionsArgs {
  processId: string;
}

export interface StepActions {
  addStep: (position: { x: number; y: number }, laneKey?: string) => Promise<void>;
  deleteSteps: (stepIds: string[]) => Promise<void>;
}

export function useStepActions({ processId }: UseStepActionsArgs): StepActions {
  const { t } = useTranslation();
  const graph = useDraftGraph(processId);
  const addStepMutation = useAddDraftStep(processId);
  const deleteStepsMutation = useDeleteDraftSteps(processId);
  const { getCachedGeneratedPositions, getCachedStepPositions } = useProcessDraftCache(processId);

  const addStepAsync = addStepMutation.mutateAsync;
  const deleteStepsAsync = deleteStepsMutation.mutateAsync;

  const addStep = useCallback(
    async (position: { x: number; y: number }, laneKey?: string) => {
      const occupied = [...getCachedStepPositions().values(), ...getCachedGeneratedPositions()];
      const resolvedPosition = resolveNonOverlappingPosition(position, occupied);
      await addStepAsync({
        position: resolvedPosition,
        ...(laneKey ? { laneKey } : {})
      });
    },
    [addStepAsync, getCachedStepPositions, getCachedGeneratedPositions]
  );

  const deleteSteps = useCallback(
    async (stepIds: string[]) => {
      // Partition the selection instead of failing it wholesale (mirrors the
      // backend's skip-unresolvable design): ids evicted from the cache by a
      // concurrent editor no longer resolve, and optimistic placeholder steps
      // resolve to a `temp-draft-step-…` id the backend's ObjectId validation
      // would 400 the entire request over.
      const backendIds: string[] = [];
      for (const stable of stepIds) {
        let backendId: string;
        try {
          backendId = graph.resolveNodeRef(stable, 'step').backendId;
        } catch (error) {
          if (error instanceof DraftNodeNotFoundError) continue;
          throw error;
        }
        if (isObjectIdHex(backendId)) backendIds.push(backendId);
      }
      if (backendIds.length === 0) return;

      // One bulk request for the whole selection — per-step DELETEs (plus
      // their settle-invalidation refetches) tripped the API rate limit on
      // large selections and left the batch half-applied.
      const result = await deleteStepsAsync(backendIds);
      if (result.failedStepIds.length > 0) {
        toast.error(t('errors.processDraft.deletePartialFailure'));
      } else if (result.notFoundStepIds.length > 0 && result.deletedNodeIds.length === 0) {
        toast.info(t('errors.processDraft.deleteStaleSelection'));
      }
    },
    [deleteStepsAsync, graph, t]
  );

  return useMemo(() => ({ addStep, deleteSteps }), [addStep, deleteSteps]);
}
